// ShowScreen.jsx - Livreur Planning
import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Badge, Table, Button, Alert } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import ClickBack from "../../components/buttons/ClickBack";
import {
  fetchLivreurSchedule,
  fetchLivreurTimeOff,
  fetchLivreurCapacity,
  initDefaultSchedule
} from "./dataService";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const DAYS_FR = {
  1: "Lundi",
  2: "Mardi",
  3: "Mercredi",
  4: "Jeudi",
  5: "Vendredi",
  6: "Samedi",
  7: "Dimanche",
};

function ShowScreen() {
  const { id } = useParams();
  const history = useHistory();
  const [livreur, setLivreur] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [timeOffs, setTimeOffs] = useState([]);
  const [capacity, setCapacity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch schedule
      const scheduleData = await fetchLivreurSchedule(id);
      setLivreur(scheduleData.livreur);
      setSchedule(scheduleData.weekly_schedule || []);

      // Fetch time-offs (approved only for display)
      const timeOffData = await fetchLivreurTimeOff(id, { status: "approved" });
      setTimeOffs(timeOffData.time_offs || []);

      // Fetch capacity
      const capacityData = await fetchLivreurCapacity(id);
      setCapacity(capacityData.capacity);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleInitSchedule = async () => {
    try {
      setLoading(true);
      await initDefaultSchedule(id);
      await fetchData(); // Refresh data
      alert("Planning par défaut initialisé avec succès");
    } catch (err) {
      console.error("Error initializing schedule:", err);
      alert("Erreur lors de l'initialisation du planning");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className="p-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger">{error}</Alert>
        <Button onClick={() => history.goBack()}>Retour</Button>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <ClickBack />
          <h2 className="mt-3">
            Planning de {livreur?.nom} {livreur?.prenom}
          </h2>
        </Col>
        <Col className="text-end">
          <Button
            variant="primary"
            onClick={() => history.push(`/livreurs/planning/${id}/edit`)}
          >
            Modifier le planning
          </Button>
        </Col>
      </Row>

      {/* Weekly Schedule */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">Planning Hebdomadaire</h5>
            </Card.Header>
            <Card.Body>
              {schedule.length === 0 ? (
                <Alert variant="warning">
                  Aucun planning configuré pour ce livreur.
                  <Button
                    variant="primary"
                    size="sm"
                    className="ms-3"
                    onClick={handleInitSchedule}
                  >
                    Initialiser un planning par défaut
                  </Button>
                </Alert>
              ) : (
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Jour</th>
                      <th>Travaille</th>
                      <th>Début</th>
                      <th>Fin</th>
                      <th>Pause</th>
                      <th>Durée pause</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.map((day) => (
                      <tr key={day.day_of_week}>
                        <td>
                          <strong>{DAYS_FR[day.day_of_week]}</strong>
                        </td>
                        <td>
                          <Badge bg={day.is_working ? "success" : "secondary"}>
                            {day.is_working ? "Oui" : "Non"}
                          </Badge>
                        </td>
                        <td>{day.is_working ? day.start_time : "-"}</td>
                        <td>{day.is_working ? day.end_time : "-"}</td>
                        <td>
                          {day.is_working && day.break_start_time
                            ? `${day.break_start_time} - ${day.break_end_time}`
                            : "-"}
                        </td>
                        <td>
                          {day.is_working && day.break_duration_minutes
                            ? `${day.break_duration_minutes} min`
                            : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Capacity */}
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header className="bg-info text-white">
              <h5 className="mb-0">Capacités</h5>
            </Card.Header>
            <Card.Body>
              {capacity ? (
                <Table borderless>
                  <tbody>
                    <tr>
                      <td><strong>Max livraisons/jour:</strong></td>
                      <td>{capacity.default_max_deliveries_per_day}</td>
                    </tr>
                    <tr>
                      <td><strong>Max distance/jour:</strong></td>
                      <td>{capacity.default_max_distance_per_day_km} km</td>
                    </tr>
                    <tr>
                      <td><strong>Vitesse moyenne:</strong></td>
                      <td>{capacity.avg_speed_kmh} km/h</td>
                    </tr>
                    <tr>
                      <td><strong>Durée arrêt moyenne:</strong></td>
                      <td>{capacity.avg_stop_duration_minutes} min</td>
                    </tr>
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted">Aucune capacité définie</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Time Off */}
        <Col md={6}>
          <Card>
            <Card.Header className="bg-warning text-dark">
              <h5 className="mb-0">Congés Approuvés</h5>
            </Card.Header>
            <Card.Body>
              {timeOffs.length === 0 ? (
                <p className="text-muted">Aucun congé approuvé</p>
              ) : (
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Du</th>
                      <th>Au</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeOffs.map((timeOff) => (
                      <tr key={timeOff.id}>
                        <td>
                          <Badge bg="secondary">{timeOff.type}</Badge>
                        </td>
                        <td>{format(new Date(timeOff.start_date), "dd/MM/yyyy")}</td>
                        <td>{format(new Date(timeOff.end_date), "dd/MM/yyyy")}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default ShowScreen;
